import React, { FC, useMemo } from 'react';

import { ReportTimeRange } from '@app/hooks/use-chart-date-intervals';
import { AggregateSelectForm, SettingsDateStep } from '@app/interfaces/report.type';
import SettingsFormBlock from '@app/pages/reports/blocks/chart-modal/settings-form.block';
import { useGetLiveReportGraphQuery } from '@app/store/api/live-reports-charts.api';
import { Loading } from '@ui';
import { useGetChatLiveReportGraphQuery } from '@app/store/api/chat-api/chat-live-reports-charts.api';
import { useOutletContext } from 'react-router';

import { useGetGptGroupsWithMetricsQuery } from '@app/store/api/gpt-metric.api';

import { userPermissions } from '@app/utils/user-permissions';
import { usePermissionCheck } from '@app/hooks/use-permission';

import { useGetChatGptGroupsWithMetricsQuery } from '@app/store/api/chat-api/gpt-metric-chat.api';

import { ReportOutletContext } from '../../report.outlet';

type SettingsMetricStep = string;
export type SettingsChartForm = {
  type: string;
  name: string;
  width: 'full' | 'half';
  range: number | string | ReportTimeRange;
  display_legend: boolean;
  colors: Array<string>;
  display_data: boolean;
  group: 'metric' | 'date';
  group2: 'metric' | 'date';
  metric: 'string';
  step?: SettingsDateStep | SettingsMetricStep;
  metric_id: string;
  aggregate: AggregateSelectForm;
  color: string;
  metric_idY2: string;
  aggregateY2: AggregateSelectForm;
  colorY2: string;
  switchY2: boolean;
};

type ModalSettingsChartBlockProps = {
  isOpen: boolean;
  chartId?: string;
  onClose: (status: boolean) => void;
  onRemove?: (status: boolean) => void;
  currentTemplate?: 'voice' | 'chat';
};

const ModalSettingsChartBlock: FC<ModalSettingsChartBlockProps> = (props) => {
  const { isOpen, onClose, chartId, onRemove } = props;
  const { currentTemplate, liveReport } = useOutletContext<ReportOutletContext>();

  const { data: setupChat, isLoading: loadingChat } = useGetChatLiveReportGraphQuery(
    { params: { id: chartId || '' } },
    { skip: currentTemplate === 'voice' || !chartId },
  );
  const { data: setupVoice, isLoading: loadingVoice } = useGetLiveReportGraphQuery(
    { params: { id: chartId || '' } },
    { skip: currentTemplate === 'chat' || !chartId },
  );
  // gpt
  const gptGptGroupMetricsAccess = usePermissionCheck({
    permissions: 'action',
    tag: userPermissions.action.actionGetGPTGroupMetrics,
  });

  const project_id = liveReport?.project.project_id || '';

  const { data: gptGroupsListVoice } = useGetGptGroupsWithMetricsQuery(
    { params: { project_id } },
    { skip: !project_id || currentTemplate === 'chat' || !!gptGptGroupMetricsAccess },
  );
  const { data: gptGroupsListChat } = useGetChatGptGroupsWithMetricsQuery(
    { params: { project_id } },
    { skip: !project_id || currentTemplate === 'voice' || !!gptGptGroupMetricsAccess },
  );

  const gptGroupsList = useMemo(
    () => ({ chat: gptGroupsListChat, voice: gptGroupsListVoice }),
    [gptGroupsListChat, gptGroupsListVoice],
  );
  const gpt_metrics = useMemo(
    () => gptGroupsList?.[currentTemplate]?.gpt_groups.flatMap((group) => group.gpt_metrics) || [],
    [currentTemplate, gptGroupsList],
  );
  // gpt

  const isLoading = loadingChat || loadingVoice;
  const setup = { chat: setupChat, voice: setupVoice };
  if (isLoading) return <Loading />;
  if (chartId && setup[currentTemplate || 'voice'])
    return (
      <SettingsFormBlock
        setup={setup[currentTemplate || 'voice']}
        isOpen={isOpen}
        gpt_metrics={gpt_metrics}
        onClose={onClose}
        onRemove={onRemove}
      />
    );
  return (
    <>
      {isOpen && <SettingsFormBlock gpt_metrics={gpt_metrics} isOpen={isOpen} onClose={onClose} />}
    </>
  );
};

export default ModalSettingsChartBlock;
